import React, { Component, ErrorInfo, ReactNode } from 'react';
import { error as logError, getSessionId } from '../index';

export interface StackScopeErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showErrorDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class StackScopeErrorBoundary extends Component<StackScopeErrorBoundaryProps, State> {
  constructor(props: StackScopeErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Log to StackScope
    logError('React Error Boundary caught error', {
      type: 'react-error',
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      sessionId: getSessionId(),
      timestamp: new Date().toISOString()
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div style={{
          padding: '20px',
          border: '1px solid #ff6b6b',
          borderRadius: '8px',
          backgroundColor: '#fff5f5',
          margin: '20px',
          fontFamily: 'system-ui, sans-serif'
        }}>
          <h2 style={{ color: '#c92a2a', marginTop: 0 }}>Something went wrong</h2>
          <p style={{ color: '#495057' }}>
            An error occurred while rendering this component. This issue has been automatically logged.
          </p>
          
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={this.handleRetry}
              style={{
                padding: '8px 16px',
                marginRight: '10px',
                backgroundColor: '#228be6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
            <button 
              onClick={this.handleReload}
              style={{
                padding: '8px 16px',
                backgroundColor: '#868e96',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reload Page
            </button>
          </div>

          {(isDevelopment || this.props.showErrorDetails) && this.state.error && (
            <details style={{ marginTop: '20px' }}>
              <summary style={{ cursor: 'pointer', color: '#868e96' }}>
                Error Details (Development)
              </summary>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                color: '#495057',
                marginTop: '10px'
              }}>
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
                {this.state.errorInfo?.componentStack && (
                  <>
                    {'\n\nComponent Stack:'}
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}